import {BrowserModule} from '@angular/platform-browser';
import {NguiAutoCompleteModule} from '@ngui/auto-complete';
import {APP_INITIALIZER, NgModule, LOCALE_ID} from '@angular/core';
import {FormsModule, ReactiveFormsModule } from '@angular/forms';
import {HttpModule} from '@angular/http';
import {KeycloakAngularModule, KeycloakService} from 'keycloak-angular';
import {initializer} from './utils/app-init';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {LeafletModule} from '@asymmetrik/ngx-leaflet';
import {LeafletMarkerClusterModule} from '@asymmetrik/ngx-leaflet-markercluster';
import {ToastModule, ToastOptions} from 'ng2-toastr/ng2-toastr';
import {HTTP_INTERCEPTORS, HttpClientModule, HttpClient} from '@angular/common/http';
import {TreeModule} from 'angular-tree-component';
import {AppRoutingModule} from './app-routing/app-routing.module';
import {AppComponent} from './app.component';
import {DashboardComponent} from './dashboard/dashboard.component';
import {NgxPaginationModule} from 'ngx-pagination';
import {AccountService} from './services/account.service';
import {GroupService} from './services/group.service';
import {AccountsListComponent} from './accounts/accounts-list/accounts-list.component';
import {AccountFormComponent} from './accounts/account-form/account-form.component';
import {AccountUpdateComponent} from './accounts/account-update/account-update.component';
import {AttachmentPointsListComponent} from './attachmentpoints/attachmentpoints-list/attachmentpoints-list.component';
import {BikesListComponent} from './bikes/bikes-list/bikes-list.component';
import {BreakdownsListComponent} from './breakdowns/breakdowns-list/breakdowns-list.component';
import {BreakdownsDetailsComponent} from './breakdowns/breakdowns-details/breakdowns-details.component';
import {BreakdownsCloseComponent} from './breakdowns/breakdowns-close/breakdowns-close.component';
import {BreakdownService} from './services/breakdown.service';
import {ContractService} from './services/contract.service';
import {GroupsListComponent} from './groups/groups-list/groups-list.component';
import {LocksListComponent} from './locks/locks-list/locks-list.component';
import {StationsListComponent} from './stations/stations-list/stations-list.component';
import {SubscriptionPlansListComponent} from './subscriptionplans/subscriptionplans-list/subscriptionplans-list.component';
import {HeaderComponent} from './include/header/header.component';
import {AuthGuardService} from './services/auth-guard.service';
import {AccountDeleteComponent} from './accounts/account-delete/account-delete.component';
import {StationMapComponent} from './dashboard/station-map/station-map.component';
import {StationUsageComponent} from './dashboard/station-usage/station-usage.component';
import {BikeUsageComponent} from './dashboard/bike-usage/bike-usage.component';
import {ParkUsageComponent} from './dashboard/park-usage/park-usage.component';
import {UserStatComponent} from './dashboard/user-stat/user-stat.component';
import {StationService} from './services/station.service';
import {StationUsageService} from './statistics/station-usage.service';
import {BikesUsageService} from './statistics/bikes-usage.service';
import {ParkUsageService} from './statistics/park-usage.service';
import {UserStatService} from './statistics/user-stat.service';
import {BikePopupComponent} from './dashboard/bike-popup/bike-popup.component';
import {HttpErrorInterceptor} from './utils/http-interceptor';
import {CustomOption} from './utils/toast-options';
import {SubscriptionplanService} from './services/subscriptionplan.service';
import {AttachmentPointService} from './services/attachment-point.service';
import {AttachmentpointsDeleteComponent} from './attachmentpoints/attachmentpoints-delete/attachmentpoints-delete.component';
import {AttachmentpointsFormComponent} from './attachmentpoints/attachmentpoints-form/attachmentpoints-form.component';
import {StationFormComponent} from './stations/station-form/station-form.component';
import {StationDeleteComponent} from './stations/station-delete/station-delete.component';
import { AttachmentpointsMultipleDeleteComponent } from './attachmentpoints/attachmentpoints-multiple-delete/attachmentpoints-multiple-delete.component';
import { AttachmentpointsGroupbindingComponent } from './attachmentpoints/attachmentpoints-groupbinding/attachmentpoints-groupbinding.component';
import { AttachmentpointsStationBindingComponent } from './attachmentpoints/attachmentpoints-station-binding/attachmentpoints-station-binding.component';
import {BikeService} from './services/bike.service';
import {BikesDeleteComponent} from './bikes/bikes-delete/bikes-delete.component';
import {BikesFormComponent} from './bikes/bikes-form/bikes-form.component';
import {NominatimService} from './services/utils/nominatim.service';
import { LockService } from './services/lock.service';
import { LocksFormComponent } from './locks/locks-form/locks-form.component';
import { LocksDeleteComponent } from './locks/locks-delete/locks-delete.component';
import { SubscriptionplansFormComponent } from './subscriptionplans/subscriptionplans-form/subscriptionplans-form.component';
import { SubscriptionplansDeleteComponent } from './subscriptionplans/subscriptionplans-delete/subscriptionplans-delete.component';
import { GroupsFormComponent } from './groups/groups-form/groups-form.component';
import { GroupsDeleteComponent } from './groups/groups-delete/groups-delete.component';
import { TripsListComponent } from './trips/trips-list/trips-list.component';
import { TripsService } from './services/trips.service';
import { TripsDetailsComponent } from './trips/trips-details/trips-details.component';
import { CloseTripsComponent } from './trips/close-trips/close-trips.component';
import {NgxMaskModule} from 'ngx-mask';
import { BikeHistoryComponent } from './bikes/bike-history/bike-history.component';
import { StationTypeTransComponent } from './stations/station-type-trans/station-type-trans.component';
import {TranslateModule, TranslateLoader, TranslateService} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import { ParametersService } from './services/parameters.service';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en-GB';
import localeFr from '@angular/common/locales/fr';
import localeEs from '@angular/common/locales/es';
import { environment } from '../environments/environment';
import { CountryPickerService } from './utils/country-picker.service';
import { AuthGuardSuperAdminService } from './services/auth-guard-super-admin.service';
import { AuthGuardAdminService } from './services/auth-guard-admin.service';
import { UnauthorizedComponent } from './errors/unauthorized/unauthorized.component';
import { AuthGuardErrorService } from './auth-guard-error.service';
import { EnumToArrayPipe } from './pipes/enum-to-array.pipe';
import { PaymentService } from './services/payment.service';
import { CentsToEurosPipe } from './pipes/cents-to-euros.pipe';
import { PaymentListComponent } from './payment/payment-list/payment-list.component';
import { PaymentRefundDialogComponent } from './payment/payment-refund-dialog/payment-refund-dialog.component';

registerLocaleData(localeFr, 'fr');
registerLocaleData(localeEs, 'es');

@NgModule({
  declarations: [
    AppComponent,
    DashboardComponent,
    GroupsListComponent,
    AccountsListComponent,
    SubscriptionPlansListComponent,
    BikesListComponent,
    StationsListComponent,
    StationFormComponent,
    StationDeleteComponent,
    AttachmentPointsListComponent,
    LocksListComponent,
    BreakdownsListComponent,
    BreakdownsDetailsComponent,
    BreakdownsCloseComponent,
    AccountFormComponent,
    AccountUpdateComponent,
    HeaderComponent,
    AccountDeleteComponent,
    StationMapComponent,
    StationUsageComponent,
    BikeUsageComponent,
    ParkUsageComponent,
    UserStatComponent,
    BikePopupComponent,
    AttachmentpointsDeleteComponent,
    AttachmentpointsFormComponent,
    AttachmentpointsMultipleDeleteComponent,
    AttachmentpointsGroupbindingComponent,
    AttachmentpointsStationBindingComponent,
    BikesDeleteComponent,
    BikesFormComponent,
    LocksFormComponent,
    LocksDeleteComponent,
    SubscriptionplansFormComponent,
    SubscriptionplansDeleteComponent,
    GroupsFormComponent,
    GroupsDeleteComponent,
    TripsListComponent,
    TripsDetailsComponent,
    CloseTripsComponent,
    BikeHistoryComponent,
    StationTypeTransComponent,
    UnauthorizedComponent,
    PaymentListComponent,
    EnumToArrayPipe,
    CentsToEurosPipe,
    PaymentRefundDialogComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    NguiAutoCompleteModule,
    HttpModule,
    AppRoutingModule,
    HttpClientModule,
    NgbModule.forRoot(),
    LeafletModule.forRoot(),
    LeafletMarkerClusterModule,
    KeycloakAngularModule,
    ToastModule.forRoot(),
    TreeModule,
    NgxPaginationModule,
    NgxMaskModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
      }
    }),
  ],
  providers: [
    AccountService,
    GroupService,
    AuthGuardService,
    AuthGuardSuperAdminService,
    AuthGuardAdminService,
    AuthGuardErrorService,
    BreakdownService,
    ContractService,
    StationService,
    BikesUsageService,
    StationUsageService,
    ParkUsageService,
    UserStatService,
    SubscriptionplanService,
    AttachmentPointService,
    BikeService,
    NominatimService,
    LockService,
    TripsService,
    ParametersService,
    PaymentService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializer,
      multi: true,
      deps: [KeycloakService, AccountService, ParametersService, TranslateService]
    },
    KeycloakService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {provide: ToastOptions, useClass: CustomOption},
    {
      provide: LOCALE_ID,
      useValue: environment.defaultLanguage,
      deps : [],
      useFactory : () => navigator.language ? navigator.language : environment.defaultLanguage
    },
    CountryPickerService
  ],
  entryComponents: [
    AccountDeleteComponent,
    AccountFormComponent,
    AccountUpdateComponent,
    StationFormComponent,
    StationDeleteComponent,
    AttachmentpointsDeleteComponent,
    AttachmentpointsFormComponent,
    AttachmentpointsMultipleDeleteComponent,
    AttachmentpointsGroupbindingComponent,
    AttachmentpointsStationBindingComponent,
    BikesDeleteComponent,
    BikesFormComponent,
    LocksFormComponent,
    LocksDeleteComponent,
    SubscriptionplansFormComponent,
    SubscriptionplansDeleteComponent,
    GroupsFormComponent,
    GroupsDeleteComponent,
    TripsDetailsComponent,
    CloseTripsComponent,
    BreakdownsDetailsComponent,
    BreakdownsCloseComponent,
    PaymentRefundDialogComponent
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
}

// required for AOT compilation
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}



// WEBPACK FOOTER //
// ./src/app/app.module.ts